/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsDistribute } from "../funcs/skillsDistribute.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SkillDistribution } from "../models/components/skilldistribution.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DistributeSkillRequest,
  DistributeSkillSecurity,
} from "../models/operations/distributeskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DistributeSkillMutationVariables = {
  request: DistributeSkillRequest;
  security?: DistributeSkillSecurity | undefined;
  options?: RequestOptions;
};

export type DistributeSkillMutationData = SkillDistribution;

export type DistributeSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * distribute skills
 *
 * @remarks
 * Create or update the active distribution of a skill to exactly one plugin or assistant. Repeating the request for the same target updates the version pin or is a no-op.
 */
export function useDistributeSkillMutation(
  options?: MutationHookOptions<
    DistributeSkillMutationData,
    DistributeSkillMutationError,
    DistributeSkillMutationVariables
  >,
): UseMutationResult<
  DistributeSkillMutationData,
  DistributeSkillMutationError,
  DistributeSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDistributeSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyDistributeSkill(): MutationKey {
  return ["@gram/client", "skills", "distribute"];
}

export function buildDistributeSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DistributeSkillMutationVariables,
  ) => Promise<DistributeSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyDistributeSkill(),
    mutationFn: function distributeSkillMutationFn({
      request,
      security,
      options,
    }): Promise<DistributeSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsDistribute(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
